// 4 · Expo / waiter board (Expo.dc.html; the pass, 1920×1080, responsive).
// Ready to run = ready tickets nobody has taken yet; On the way = a ready
// ticket someone acknowledged ("on it"); Served = served tickets. All from the
// server: GET /kitchen/expo, /alerts (who acknowledged), /kitchen/tickets?view=done.
// A register / kiosk / online order is ONE takeout card for the whole order
// (Expo.dc.html): "Customer here" acknowledges it, "Picked up" serves every
// ticket of it at once (POST /kitchen/orders/{id}/picked-up) once all are ready.
import React, { useMemo, useState } from 'react';
import { api, errorText, newKey } from '../lib/api';
import { channelLine, clock, elapsed, isChannelTicket, modifierText, plural, secondsSince, shortName, ticketTitle } from '../lib/format';
import { useLive } from '../lib/useLive';
import { useDiningSession } from '../lib/session';
import { chime, useSoundPref } from '../lib/sound';
import { C, FONT_TIGHT } from '../theme/tokens';
import { ALERT_EVENTS } from '../components/AlertCenter';
import { BottomNav, DarkTopBar, UserMenu } from '../components/Shell';
import { Clock, ErrorNote, FullError, LiveIndicator, Skeleton, StreamBanner, useNow, useToast } from '../components/ui';
import { IconCheck, IconSoundOff, IconSoundOn } from '../components/icons';

const SERVED_WINDOW_MIN = 30;

function Card({ c, now, onAck, onServe, busy, canServe }) {
  const ready = c.stage === 'ready';
  const served = c.stage === 'served';
  if (served) {
    return (
      <article style={{ background: '#0F141B', border: `1.5px solid ${C.line}`, borderRadius: 16, padding: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, opacity: 0.85 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontFamily: FONT_TIGHT, fontSize: 18, fontWeight: 800, color: C.textHi }}>{c.title}</span>
          <span style={{ fontSize: 14, color: C.textMid }}>{c.sub}</span>
        </div>
        <span style={{ height: 32, padding: '0 10px', borderRadius: 10, background: 'rgba(34,197,94,.14)', color: C.greenText, display: 'flex', alignItems: 'center' }}><IconCheck size={16} /></span>
      </article>
    );
  }
  const age = secondsSince(c.readyAt, now);
  return (
    <article className="c29-in" style={{ background: ready ? '#1A1408' : '#121922', border: `1.5px solid ${ready ? '#B7791F' : '#2A3A55'}`, borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontFamily: FONT_TIGHT, fontSize: 24, fontWeight: 800, color: C.textHi, letterSpacing: '-0.3px' }}>{c.title}</span>
          <span style={{ fontSize: 14, color: C.textMid }}>{c.sub}</span>
        </div>
        <span style={{ height: 32, padding: '0 10px', borderRadius: 10, background: ready ? 'rgba(245,158,11,.18)' : 'rgba(59,130,246,.18)', color: ready ? (age > 180 ? '#FCA5A5' : C.amberHi) : '#93C5FD', fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          {ready ? `Ready ${elapsed(age)}` : 'On the way'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid #222A35' }}>
        {c.items.map((i) => (
          <div key={i.id} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, padding: '9px 0', borderBottom: '1px solid #222A35' }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#F1F5F9' }}>{i.name}{i.quantity > 1 ? ` ×${i.quantity}` : ''}</span>
            <span style={{ fontSize: 14, color: C.textMid, whiteSpace: 'nowrap', textAlign: 'right' }}>{[i.seat_number ? `Seat ${i.seat_number}` : null, modifierText(i)].filter(Boolean).join(' · ')}</span>
          </div>
        ))}
      </div>
      {c.pending && <span style={{ fontSize: 13.5, color: '#93C5FD' }}>{c.pending}</span>}
      <div style={{ display: 'flex', gap: 10 }}>
        {ready && c.alert && (
          <button type="button" className="c29-press" onClick={() => onAck(c)} disabled={busy}
            style={{ flex: 1, height: 56, border: 'none', borderRadius: 14, background: C.amber, color: '#1F1300', fontSize: 17, fontWeight: 800 }}>{c.takeout ? 'Customer here' : 'Acknowledge'}</button>
        )}
        {canServe && (
          <button type="button" className="c29-press" onClick={() => onServe(c)} disabled={busy || c.waiting}
            title={c.waiting ? 'Part of this order is still cooking' : undefined}
            style={{ flex: 1, height: 56, borderRadius: 14, border: '1.5px solid #3B4757', background: ready ? 'transparent' : '#F8FAFC', color: ready ? C.text : C.floor, fontSize: 17, fontWeight: 800, opacity: c.waiting ? 0.5 : 1 }}>
            {busy ? '…' : c.takeout ? 'Picked up' : 'Served'}
          </button>
        )}
      </div>
    </article>
  );
}

// accountMenu: the Expo PWA (apps/expo, expo.<domain>) signs in with the restaurant
// account and shows its own sign-out here; it has no other dining screens (no bottom nav).
export default function ExpoScreen({ accountMenu = null }) {
  const { me, perms } = useDiningSession();
  const toast = useToast();
  const now = useNow(1000);
  const [soundOn, toggleSound] = useSoundPref('dining_expo_sound');
  const [mine, setMine] = useState(false);
  const [busy, setBusy] = useState(null);
  const board = useLive(async () => {
    const [expo, alerts, done] = await Promise.all([
      api.get('/dining/kitchen/expo'),
      api.get('/dining/alerts', { include_acknowledged: 'true' }).catch(() => ({ alerts: [] })),
      api.get('/dining/kitchen/tickets', { view: 'done', limit: 40 }),
    ]);
    return { groups: expo.groups, alerts: alerts.alerts || [], done: done.tickets };
  }, [], {
    types: [...new Set([...ALERT_EVENTS, 'order.sent', 'order.picked_up', 'ticket.preparing', 'ticket.item_updated', 'item.voided', 'item.updated', 'ticket.acknowledged'])],
    onEvent: (e) => { if (e.type === 'ticket.ready' && soundOn) chime('ready'); },
  });

  const isWaiter = me?.role === 'waiter' || me?.role === 'supervisor';
  const filterMine = (serverId) => !mine || serverId === me?.id;
  const canServe = perms.can('orders.serve') || perms.can('expo.write') || perms.can('kds.write');

  const columns = useMemo(() => {
    const data = board.data;
    if (!data) return null;
    const alertByTicket = {};
    data.alerts.filter((a) => a.kind === 'food_ready').forEach((a) => { alertByTicket[a.ticket_id] = a; });
    const ready = [];
    const way = [];
    const pendingOf = (cooking) => {
      const names = cooking.flatMap((x) => x.items.filter((i) => !i.voided && i.status !== 'ready' && i.status !== 'served').map((i) => i.name));
      return names.length ? `Still cooking: ${names.slice(0, 4).join(', ')}${names.length > 4 ? ` +${names.length - 4}` : ''}` : null;
    };
    data.groups.filter((g) => filterMine(g.server_staff_id)).forEach((g) => {
      const readyTickets = g.tickets.filter((t) => t.status === 'ready');
      const cooking = g.tickets.filter((t) => t.status === 'new' || t.status === 'preparing');
      if (isChannelTicket(g)) {
        // one takeout card per order, whichever stations cooked it
        if (!readyTickets.length) return;
        const alerts = readyTickets.map((t) => alertByTicket[t.id]).filter(Boolean);
        const by = [...new Set(alerts.flatMap((a) => (a.acknowledged_by || []).map((x) => shortName(x.name))).filter(Boolean))];
        const items = readyTickets.flatMap((t) => t.items.filter((i) => !i.voided && i.status === 'ready'));
        const live = g.tickets.filter((t) => t.status !== 'cancelled');
        const card = {
          key: `order-${g.order_id}`, takeout: true, order: g, ticket: readyTickets[0], alerts, alert: alerts[0],
          readyAt: readyTickets.map((t) => t.ready_at || t.updated_at).sort()[0],
          title: `${ticketTitle(g)} — ${g.all_ready ? 'bagged' : `${readyTickets.length} of ${live.length} ready`}`,
          sub: by.length ? `Customer here · ${by.join(', ')}` : channelLine(g),
          items, pending: pendingOf(cooking), waiting: !g.all_ready, stage: by.length ? 'way' : 'ready',
        };
        (by.length ? way : ready).push(card);
        return;
      }
      readyTickets.forEach((t) => {
        const a = alertByTicket[t.id];
        const by = (a?.acknowledged_by || []).map((x) => shortName(x.name)).filter(Boolean);
        const items = t.items.filter((i) => !i.voided && i.status === 'ready');
        const card = {
          key: t.id, ticket: t, alert: a, readyAt: t.ready_at || t.updated_at,
          title: `Table ${t.table_number ?? '—'} — ${plural(items.reduce((n, i) => n + i.quantity, 0), 'item')} ready`,
          sub: by.length ? `Picked up by ${by.join(', ')}` : [shortName(g.server_name), `Round ${g.round_no}`, t.station?.name].filter(Boolean).join(' · '),
          items,
          pending: pendingOf(cooking),
          stage: by.length ? 'way' : 'ready',
        };
        (by.length ? way : ready).push(card);
      });
    });
    ready.sort((a, b) => String(a.readyAt).localeCompare(String(b.readyAt)));
    const count = (list) => plural(list.flatMap((t) => t.items.filter((i) => !i.voided)).reduce((n, i) => n + i.quantity, 0), 'item');
    const recent = data.done.filter((t) => t.status === 'served' && filterMine(t.server_staff_id))
      .filter((t) => !t.served_at || secondsSince(t.served_at, now) < SERVED_WINDOW_MIN * 60);
    const pickedUp = {};
    const served = [];
    recent.forEach((t) => {
      if (!isChannelTicket(t)) {
        served.push({
          key: t.id, stage: 'served', title: `Table ${t.table_number ?? '—'} · ${count([t])}`,
          sub: `Served ${clock(t.served_at)} · Round ${t.round_no}${t.station?.name ? ` · ${t.station.name}` : ''}`,
        });
        return;
      }
      if (pickedUp[t.order_id]) { pickedUp[t.order_id].tickets.push(t); return; }
      pickedUp[t.order_id] = { key: `order-${t.order_id}`, stage: 'served', t, tickets: [t] };
      served.push(pickedUp[t.order_id]);
    });
    served.forEach((c) => {
      if (!c.t) return;
      c.title = `${ticketTitle(c.t)} · ${count(c.tickets)}`;
      c.sub = [`Picked up ${clock(c.t.served_at)}`, shortName(c.t.customer_name)].filter(Boolean).join(' · ');
    });
    return [
      { key: 'ready', title: 'Ready to run', color: C.amber, hint: 'Oldest first', cards: ready },
      { key: 'way', title: 'On the way', color: C.blueHi, hint: 'Picked up', cards: way },
      { key: 'served', title: 'Served', color: '#64748B', hint: `Last ${SERVED_WINDOW_MIN} min`, cards: served },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.data, mine, now]);

  const ack = async (c) => {
    setBusy(c.key);
    try {
      for (const a of c.alerts || [c.alert]) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/dining/alerts/${a.seq}/acknowledge`);
      }
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      board.reload();
    }
  };

  const serve = async (c) => {
    setBusy(c.key);
    try {
      if (c.takeout) {
        await api.post(`/dining/kitchen/orders/${c.order.order_id}/picked-up`, {}, { idempotencyKey: newKey('picked-up') });
        toast.ok(`${ticketTitle(c.order)} picked up`);
        return;
      }
      await api.post(`/dining/kitchen/tickets/${c.ticket.id}/serve`, { expected_version: c.ticket.version }, { idempotencyKey: newKey('serve') });
      toast.ok(`Table ${c.ticket.table_number} served`);
    } catch (e) {
      toast.error(e.isStale ? 'Changed on another screen - showing the latest.' : errorText(e));
    } finally {
      setBusy(null);
      board.reload();
    }
  };

  return (
    <div className="c29 c29-dark" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <DarkTopBar height={84} logo={44} title="Expo · The pass" sub="Ready food for servers and front desk"
        right={(
          <>
            <button type="button" onClick={toggleSound} aria-pressed={!soundOn} className="c29-press"
              style={{ height: 52, padding: '0 16px', borderRadius: 12, border: `1px solid ${C.line2}`, background: C.panel2, color: C.text, fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
              {soundOn ? <IconSoundOn size={22} /> : <IconSoundOff size={22} />}{soundOn ? 'Sound on' : 'Muted'}
            </button>
            <LiveIndicator status={board.streamStatus} size={15} />
            <Clock size={34} color={C.textHi} weight={800} />
            {accountMenu || <UserMenu me={me} />}
          </>
        )}>
        <div role="tablist" aria-label="Filter" style={{ display: 'flex', gap: 4, padding: 5, background: C.panel2, borderRadius: 14, flex: 'none' }}>
          {[{ v: false, l: 'All tables' }, ...(isWaiter ? [{ v: true, l: `My tables · ${shortName(me?.name)}` }] : [])].map((o) => (
            <button key={String(o.v)} type="button" role="tab" aria-selected={mine === o.v} onClick={() => setMine(o.v)}
              style={{ height: 50, padding: '0 20px', border: 'none', borderRadius: 10, background: mine === o.v ? C.line2 : 'transparent', color: mine === o.v ? C.textHi : C.textMid, fontSize: 16, fontWeight: mine === o.v ? 700 : 600, whiteSpace: 'nowrap' }}>{o.l}</button>
          ))}
        </div>
      </DarkTopBar>
      <StreamBanner status={board.streamStatus} onRetry={board.reload} />

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 20, padding: '20px 24px' }}>
        {board.loading && !board.data && [0, 1, 2].map((i) => <Skeleton key={i} height="100%" style={{ flex: 1, borderRadius: 20 }} />)}
        {board.error && !board.data && <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FullError error={board.error} onRetry={board.reload} title="Could not load the pass" /></div>}
        {columns && (
          <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            {columns.map((col) => (
              <section key={col.key} style={{ background: '#10151C', borderRadius: 20, padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, minHeight: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 4px 8px' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 4, background: col.color }} />
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.textHi }}>{col.title}</h2>
                  <span style={{ minWidth: 30, height: 30, padding: '0 8px', borderRadius: 15, background: C.line, color: C.textHi, fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{col.cards.length}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 13, color: C.textDim }}>{col.hint}</span>
                </div>
                <div className="c29-scroll" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {col.cards.map((c) => <Card key={c.key} c={c} now={now} busy={busy === c.key} canServe={canServe} onAck={ack} onServe={serve} />)}
                  {!col.cards.length && (
                    <div style={{ padding: '36px 12px', textAlign: 'center', fontSize: 14, color: C.textDim, lineHeight: 1.5 }}>
                      {col.key === 'ready' ? 'Nothing waiting at the pass.' : col.key === 'way' ? 'Food someone is carrying shows here.' : 'Nothing served in the last 30 minutes.'}
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
        {board.error && board.data && <div style={{ position: 'fixed', left: 24, right: 24, bottom: 90 }}><ErrorNote error={`Showing the last board - ${errorText(board.error)}`} onRetry={board.reload} compact /></div>}
      </div>
      {!accountMenu && <BottomNav me={me} active="expo" />}
    </div>
  );
}
